import { Fragment, useState } from "react";
import { FilterFunnelButton } from "../components/BoardFilterControl";
import { BoothNavButton, resolveMapHall } from "../components/BoothNavButton";
import { FilterPill } from "../components/FilterPill";
import { StatusBlock } from "../components/StatusBlock";
import type { CompanyBrowserTarget } from "../company-browser/types";
import {
  floorFilterChips,
  floorFilterCount,
  floorFiltersAreActive,
  type CampaignFilters,
} from "../campaignFilters";
import { CloseOutNoteCard } from "./CloseOutNoteCard";
import type { RatingBoardRow } from "../ratings";
import type { LoadState } from "../types";

export function workPlanSummary(
  board: LoadState<unknown>,
  unworkedCount: number,
  closedCount: number,
): string {
  if (board.status === "loading" || board.status === "idle") return "Loading plan…";
  return `${unworkedCount} unworked${closedCount ? ` · ${closedCount} closed` : ""}`;
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest("button, select, a, input, textarea, label"));
}

export function WorkMyPlan({
  eventKey,
  actor,
  rows,
  board,
  closedAccountIds,
  filters,
  filterDrawerOpen = false,
  onOpenFilters,
  onShowRoute,
  routeBusy = false,
  onGoToBooth,
  onClosedOut,
  onOpenCompany,
  sharedBoothIds,
}: {
  eventKey: string;
  actor: string;
  rows: RatingBoardRow[];
  board: LoadState<unknown>;
  closedAccountIds: ReadonlySet<string>;
  filters: CampaignFilters;
  filterDrawerOpen?: boolean;
  onOpenFilters: () => void;
  onShowRoute: () => void;
  routeBusy?: boolean;
  onGoToBooth: (booth: string, label: string, hall?: string | null) => void;
  onClosedOut: () => void;
  onOpenCompany?: (target: CompanyBrowserTarget) => void;
  sharedBoothIds?: ReadonlySet<string>;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const unworked = rows.filter((row) => !closedAccountIds.has(row.account_id));
  const chips = floorFilterChips(filters);
  const filterCount = floorFilterCount(filters);
  const canShowRoute = unworked.some((row) => Boolean(row.booth?.trim()));
  const tableState =
    board.status === "ready" && unworked.length === 0
      ? { status: "empty" as const, message: "No unworked walk targets. Mark accounts with Walk on Account View." }
      : board;

  return (
    <section className="work-plan">
      <h2 className="visually-hidden">Walk plan</h2>
      <div className="work-plan-toolbar">
        <div className="work-plan-filters">
          {chips.length ? (
            chips.map((chip) => (
              <FilterPill
                key={chip.id}
                className="work-plan-filter-chip"
                label={chip.label}
                pressed
                onClick={onOpenFilters}
              />
            ))
          ) : (
            <span className="muted work-plan-filter-empty">All walk targets</span>
          )}
          <FilterFunnelButton
            open={filterDrawerOpen}
            active={floorFiltersAreActive(filters)}
            count={filterCount}
            onClick={onOpenFilters}
          />
        </div>
        <button
          type="button"
          className="primary work-plan-route"
          onClick={onShowRoute}
          disabled={!canShowRoute || routeBusy}
        >
          {routeBusy ? "Routing…" : "Show My Route"}
        </button>
      </div>
      {tableState.status !== "ready" && unworked.length === 0 ? (
        <StatusBlock state={tableState} loadingLabel="Loading targets…" />
      ) : (
        <div className="rating-table-wrap">
          <table className="rating-table">
            <thead>
              <tr>
                <th className="company-cell">Company</th>
                <th className="col-compact">Booth</th>
                <th className="col-compact">AI score</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {unworked.map((row) => {
                const expanded = expandedId === row.account_id;
                return (
                  <Fragment key={row.account_id}>
                    <tr
                      className={onOpenCompany ? "clickable-row" : undefined}
                      onClick={(event) => {
                        if (!onOpenCompany || isInteractiveTarget(event.target)) return;
                        onOpenCompany({ account_id: row.account_id });
                      }}
                    >
                      <td className="company-cell">
                        <strong>{row.name || row.booth || "Account"}</strong>
                        <div className="muted">{row.domain}</div>
                      </td>
                      <td className="col-compact">
                        <BoothNavButton
                          booth={row.booth}
                          shared={Boolean(row.booth && sharedBoothIds?.has(row.booth))}
                          onClick={
                            row.booth
                              ? (booth) => onGoToBooth(booth, row.name || booth, resolveMapHall(row.hall, booth))
                              : undefined
                          }
                        />
                      </td>
                      <td className="col-compact">{row.ai_rating ?? "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => setExpandedId(expanded ? null : row.account_id)}
                        >
                          {expanded ? "Hide" : "Close out"}
                        </button>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td colSpan={4}>
                          <CloseOutNoteCard
                            eventKey={eventKey}
                            accountId={row.account_id}
                            boothNumber={row.booth}
                            actor={actor}
                            onWalkChanged={() => {
                              setExpandedId(null);
                              onClosedOut();
                            }}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
